import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import AdminDashboardOverview from './AdminDashboardOverview';
import UserManagement from './UserManagement';
import LocalUserManagement from './LocalUserManagement';
import LocalUsers from './LocalUsers';
import WebhookApiKeys from './WebhookApiKeys';
import WebhookCalls from './WebhookCalls';
import Configuration from './Configuration';
// import PendingApproval from './PendingApproval';
// import WhitelistManagement from './WhitelistManagement';
import ChatHistory from './ChatHistory';
import { AdManagement } from './AdManagement';
import { AdAnalyticsDashboard } from './AdAnalyticsDashboard';
import { useAuth } from '../contexts/AuthContext';
import { Menu } from 'lucide-react';
import { Button } from './ui/button';

const AdminDashboard: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  // Redirect if not admin or super-admin
  if (user?.role !== 'admin' && user?.role !== 'super-admin') {
    return (
      <div className='flex justify-center items-center min-h-screen px-4'>
        <h1 className='text-xl text-red-600 font-semibold text-center'>
          Access Denied. Admin privileges required.
        </h1>
      </div>
    );
  }

  return (
    <div className='flex min-h-screen bg-gray-50'>
      {/* Mobile menu button */}
      <div className='lg:hidden fixed top-4 left-4 z-50'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => setSidebarOpen(true)}
          className='bg-white shadow-md'
        >
          <Menu className='h-4 w-4' />
        </Button>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className='lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40'
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <AdminSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main content */}
      <main className='flex-1 lg:ml-60 p-4 sm:p-6 pt-16 lg:pt-6'>
        <Routes>
          <Route path='dashboard' element={<AdminDashboardOverview />} />
          <Route path='users' element={<UserManagement />} />
          {/* Only allow super-admin to access local users */}
          <Route
            path='local-users'
            element={
              user?.role === 'super-admin' ? (
                <LocalUserManagement />
              ) : (
                <div className='flex justify-center items-center min-h-screen px-4'>
                  <h1 className='text-xl text-red-600 font-semibold text-center'>
                    Access Denied. Super Admin privileges required.
                  </h1>
                </div>
              )
            }
          />
          {/* New hierarchical routes for configuration sub-items */}
          <Route
            path='configuration/local-users'
            element={
              user?.role === 'super-admin' ? (
                <LocalUsers />
              ) : (
                <div className='flex justify-center items-center min-h-screen px-4'>
                  <h1 className='text-xl text-red-600 font-semibold text-center'>
                    Access Denied. Super Admin privileges required.
                  </h1>
                </div>
              )
            }
          />
          <Route
            path='configuration/webhook-api-keys'
            element={
              user?.role === 'super-admin' ? (
                <WebhookApiKeys />
              ) : (
                <div className='flex justify-center items-center min-h-screen px-4'>
                  <h1 className='text-xl text-red-600 font-semibold text-center'>
                    Access Denied. Super Admin privileges required.
                  </h1>
                </div>
              )
            }
          />
          <Route
            path='configuration/webhook-calls'
            element={
              user?.role === 'super-admin' ? (
                <WebhookCalls />
              ) : (
                <div className='flex justify-center items-center min-h-screen px-4'>
                  <h1 className='text-xl text-red-600 font-semibold text-center'>
                    Access Denied. Admin privileges required.
                  </h1>
                </div>
              )
            }
          />
          <Route path='chats' element={<ChatHistory />} />
          <Route path='ads' element={<AdManagement />} />
          <Route path='analytics' element={<AdAnalyticsDashboard />} />
          {/* Only allow super-admin to access configuration */}
          <Route
            path='configuration'
            element={
              user?.role === 'super-admin' ? (
                <Configuration />
              ) : (
                <div className='flex justify-center items-center min-h-screen px-4'>
                  <h1 className='text-xl text-red-600 font-semibold text-center'>
                    Access Denied. Super Admin privileges required.
                  </h1>
                </div>
              )
            }
          />
          <Route path='/' element={<Navigate to='dashboard' replace />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
